// Bois de chauffage (lemn de foc) vendu en Roumanie — demande de Ferdinand du
// 2026-10-09 : une declaration de chargement encadree, et sur l'aviz une ligne
// « charge a la demande du client » avec le chauffeur et la plaque.
//
// Depuis le 2026-10-12, la declaration ne va QUE sur la facture (Ferdinand : le
// probleme ne concerne que les camionnettes et camions de bois de chauffage, et
// la vente se fait directement en facture) — les 5 generateurs de facture
// (invoicing/, broker/invoicing/, les deux InvoiceDownload et FirewoodInvoiceView),
// avec la ligne chauffeur dessous. Les devis ne l'impriment plus ; l'aviz
// (transport/DownloadAllReception) garde sa ligne chauffeur.
//
// Police : les PDF n'ont qu'Amiri « normal ». `setFontType("bold")` retombe sur
// Times-Bold (WinAnsi : ni ă, ni ș/ț, ni tiret demi-cadratin) et un simple
// `setFont("Amiri")` ne restaure PAS Amiri (le style actif reste « bold »).
// Tout est donc dessine en Amiri normal, avec `setFont("Amiri", "normal")`.
import { hasFirewoodLine } from "@/helpers/firewood.js";
import { toAmiriRomanian } from "@/helpers/roDiacritics.js";

/** Pays de facturation « Romania » (libelle countries.name_en ou label d'adresse). */
export function isRomaniaCountry(country) {
  return /Romania/i.test(String(country || ""));
}

/**
 * Vente « bois de chauffage en Roumanie » : au moins UNE ligne lemn de foc
 * (src/helpers/firewood.js) ET une adresse de facturation en Roumanie.
 * @param {Array} products `user_data.products` (lignes avec operations_products.idproduct)
 * @param {string} billingCountry `addressbilling.country` ou le label de l'adresse
 */
export function isRomaniaFirewoodSale(products, billingCountry) {
  return isRomaniaCountry(billingCountry) && hasFirewoodLine(products);
}

/**
 * Facture : pays de facturation, ou a defaut la serie RO (une facture de la
 * borne sans adresse de facturation reste une vente roumaine).
 */
export function invoiceBillingCountry(addressbilling, invoiceNo) {
  const country = addressbilling && addressbilling.country;
  if (country) return country;
  return /^RO/i.test(String(invoiceNo || "")) ? "Romania" : "";
}

/**
 * Terme de depart : le client enleve et transporte lui-meme (FCA, FCA Teiu, EXW).
 * Meme test que isPickupIncoterm des ecrans devis, plus EXW.
 */
export function isDepartureIncoterm(incoterms) {
  const inco = String(incoterms || "").toUpperCase();
  return /^\s*(EXW|FCA)\b/.test(inco) || inco.includes("FREE CARRIER") || inco.includes("EX WORKS");
}

/**
 * La declaration de chargement (et la ligne chauffeur de l'aviz) n'ont de sens
 * que si le CLIENT transporte : vente bois de chauffage en Roumanie ET terme de
 * depart. Les ventes de bois livrees DAP par un transporteur (cas courant en
 * prod : RO2026_01511, _01501...) ne la portent pas.
 */
export function needsFirewoodDeclaration(products, billingCountry, incoterms) {
  return isRomaniaFirewoodSale(products, billingCountry) && isDepartureIncoterm(incoterms);
}

// Texte de Ferdinand, verbatim (sans diacritiques, comme fourni). Tiret ASCII
// dans le titre : Amiri n'a pas forcement le demi-cadratin.
export const FIREWOOD_LOADING_DECLARATION = {
  title: toAmiriRomanian("DECLARATIE SI EXONERARE DE RASPUNDERE - INCARCARE"),
  body: toAmiriRomanian(
    "Cumparatorul, care efectueaza transportul cu vehiculul propriu sau aflat in folosinta sa, " +
      "solicita expres Xylo Impact SRL sa incarce marfa in cantitatea stabilita exclusiv de Cumparator. " +
      "Cumparatorul raspunde integral, in calitate de transportator, de respectarea maselor si " +
      "dimensiunilor maxime admise ale vehiculului (OG 43/1997) si de siguranta transportului. " +
      "Cumparatorul renunta la orice pretentie impotriva Xylo Impact SRL si se obliga sa despagubeasca " +
      "Xylo Impact SRL pentru orice amenda, tarif de despagubire, cost de transbordare sau prejudiciu, " +
      "inclusiv fata de terti, rezultat din depasirea maselor admise ale vehiculului. " +
      "Livrarea se face FCA Teiu; riscul se transfera Cumparatorului la finalizarea incarcarii."
  ),
  // Ajout de Ferdinand du 2026-10-12, verbatim, second paragraphe du cadre.
  sumal: toAmiriRomanian(
    "SUMAL 2.0 – Cumparatorul si/sau transportatorul garanteaza exactitatea datelor furnizate " +
      "si introduse in SUMAL 2.0. Transportatorul inregistrat in SUMAL 2.0 respecta metodologia " +
      "si utilizarea corecta a aplicatiei pe tot parcursul transportului."
  ),
};

// Aviz de insotire a marfii (page d'une facture bois de chauffage).
export const AVIZ_FIREWOOD_LOADING_LINE =
  "Incarcat la cererea si conform instructiunilor cumparatorului.";
export const AVIZ_INCOTERM_LABEL = "Conditii de livrare (Incoterms): ";

/** Interligne courant de jsPDF en mm (taille active x facteur 1,15). */
export function pdfLineHeightMm(doc) {
  return doc.getLineHeight() / doc.internal.scaleFactor;
}

/**
 * La « zone de signature » du devis n'est pas un cadre : c'est la queue de la
 * chaine CGV par langue, apres son dernier "\n\n\n" (« Semnătura clientului: »,
 * « Signature du Client: »...). On la detache pour pouvoir glisser un bloc entre
 * les CGV et la signature. Sans separateur : tout est texte, pas de signature.
 */
export function splitSignatureLine(cgv) {
  const text = cgv ? String(cgv) : "";
  const idx = text.lastIndexOf("\n\n\n");
  if (idx === -1) return { text, signature: "" };
  return { text: text.slice(0, idx), signature: text.slice(idx + 3) };
}

const PAD = 1.5; // marge interieure du cadre (mm)
const TITLE_PT = 8;
const BODY_PT = 7;

// Un seul calcul de mise en page, avec ou sans dessin : la mesure sert a decider
// du renvoi en page 2 AVANT de dessiner. Renvoie le y du bas du cadre (mm).
// Police + taille sont posees AVANT splitTextToSize, qui mesure avec la police
// active. Rend la main en Amiri normal 7 pt (taille du bas de page des devis).
function layoutDeclaration(doc, x, y, width, draw) {
  const previousSize = doc.getFontSize();
  doc.setFont("Amiri", "normal");
  doc.setFontSize(TITLE_PT);
  const titleH = pdfLineHeightMm(doc);
  doc.setFontSize(BODY_PT);
  const lineH = pdfLineHeightMm(doc);
  const lines = doc.splitTextToSize(FIREWOOD_LOADING_DECLARATION.body, width - 2 * PAD);
  const sumalLines = doc.splitTextToSize(FIREWOOD_LOADING_DECLARATION.sumal, width - 2 * PAD);
  const titleBaseline = y + PAD + titleH * 0.8;
  const bodyBaseline = titleBaseline + 1 + lineH;
  // Paragraphe SUMAL : une demi-ligne d'ecart sous le texte de la declaration.
  const sumalBaseline = bodyBaseline + (lines.length - 1) * lineH + lineH * 1.5;
  const bottom = sumalBaseline + (sumalLines.length - 1) * lineH + lineH * 0.35 + PAD;
  if (draw) {
    doc.rect(x, y, width, bottom - y);
    doc.setFontSize(TITLE_PT);
    doc.text(x + PAD, titleBaseline, FIREWOOD_LOADING_DECLARATION.title);
    doc.setFontSize(BODY_PT);
    doc.text(x + PAD, bodyBaseline, lines.join("\n"));
    doc.text(x + PAD, sumalBaseline, sumalLines.join("\n"));
  }
  // Rend la taille trouvee en entrant (8 pt sur les factures).
  doc.setFontSize(previousSize);
  return bottom;
}

/** Hauteur du bloc en mm, sans rien dessiner. */
export function measureFirewoodDeclaration(doc, width) {
  return layoutDeclaration(doc, 0, 0, width, false);
}

/** Dessine le bloc encadre (titre + texte) ; renvoie le y du bas du cadre. */
export function drawFirewoodDeclaration(doc, x, y, width) {
  return layoutDeclaration(doc, x, y, width, true);
}

const DRIVER_PT = 8;

// Ligne « charge a la demande du client » + chauffeur / plaque / signature, sous
// la declaration d'une facture. Chauffeur et plaques repris de la facture (la
// borne les enregistre) ; sinon des tirets, a remplir a la main. Renvoie le y
// du bas (mm) ; rend la taille trouvee en entrant.
function layoutDriverLines(doc, x, y, data, draw) {
  const previousSize = doc.getFontSize();
  doc.setFont("Amiri", "normal");
  doc.setFontSize(DRIVER_PT);
  const lh = pdfLineHeightMm(doc);
  const first = y + lh * 1.1;
  const second = first + lh * 1.6;
  if (draw) {
    const d = data || {};
    const plates = [d.truckPlate, d.trailerPlate]
      .filter((v) => v && String(v).trim())
      .join(" / ");
    doc.text(x, first, AVIZ_FIREWOOD_LOADING_LINE);
    doc.text(x, second, "Nume sofer: " + (d.driver ? toAmiriRomanian(String(d.driver)) : "______________________"));
    doc.text(x + 62, second, "Nr. inmatriculare: " + (plates || "________________"));
    doc.text(x + 128, second, "Semnatura sofer: ______________");
  }
  doc.setFontSize(previousSize);
  return second + lh * 0.4;
}

/** Hauteur (mm) de la ligne chauffeur, sans rien dessiner. */
export function measureFirewoodDriverLines(doc) {
  return layoutDriverLines(doc, 0, 0, null, false);
}

/** Dessine la ligne « Incarcat la cererea... » + chauffeur / plaque / signature. */
export function drawFirewoodDriverLines(doc, x, y, data) {
  return layoutDriverLines(doc, x, y, data, true);
}
